/**
 * API client.
 *
 * Same-origin in dev (Vite proxies /api) and in production (nginx proxies /api/),
 * so the session cookie and the server's Origin check behave identically in
 * both. Every mutating call sends JSON, which the server requires as its
 * third CSRF layer.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : null;

  if (!res.ok) {
    const message =
      (payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    const code = payload && typeof payload === 'object' && 'code' in payload ? String((payload as { code: unknown }).code) : undefined;
    const details = payload && typeof payload === 'object' && 'details' in payload ? (payload as { details: unknown }).details : undefined;
    throw new ApiError(res.status, message, code, details);
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T,>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T,>(path: string) => request<T>('DELETE', path),
};

/**
 * Resolves a short-lived signed URL for a receipt. The API never serves the
 * bytes itself, so every view goes through one of these and they expire in
 * five minutes -- fetch on display, do not cache in state for long.
 */
export async function receiptUrl(
  receiptId: string,
  variant: 'full' | 'thumb' = 'thumb',
): Promise<string | null> {
  const { url } = await api.get<{ url: string | null }>(`/receipts/${receiptId}/url?variant=${variant}`);
  return url;
}

// ── domain types ─────────────────────────────────────────────────────────────

export type UserRole = 'staff' | 'finance' | 'admin';
export type ReportStatus =
  | 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | 'paid';

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  mustChangePassword: boolean;
}

export interface ReportSummary {
  id: string;
  type: 'monthly' | 'travel';
  status: ReportStatus;
  title: string;
  periodMonth: string | null;
  submittedAt: string | null;
  submittedTotalAmount: string | null;
  createdAt: string;
  ownerName: string;
  ownerEmail: string;
  tripName: string | null;
  tripDestination: string | null;
  tripStartDate: string | null;
  tripEndDate: string | null;
  lineCount: number;
  currentTotal: string;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  glCode: string;
  requiresReceipt: boolean;
  requiresReceiptOver: string | null;
  appliesToKind: 'expense' | 'mileage' | 'per_diem';
}

export interface LineItem {
  id: string;
  kind: 'expense' | 'mileage' | 'per_diem';
  expense_date: string;
  amount: string;
  merchant: string | null;
  description: string | null;
  notes: string | null;
  receipt_id: string | null;
  category_id: string;
  category_name: string;
  category_code: string;
  from_location: string | null;
  to_location: string | null;
  miles: string | null;
  rate_per_mile: string | null;
  round_trip: boolean | null;
  entry_date: string | null;
  day_type: string | null;
  proration_percent: string | null;
  breakfast_claimed: boolean | null;
  lunch_claimed: boolean | null;
  dinner_claimed: boolean | null;
  incidentals_claimed: boolean | null;
  breakfast_amount: string | null;
  lunch_amount: string | null;
  dinner_amount: string | null;
  incidentals_amount: string | null;
}

export interface Receipt {
  id: string;
  status: 'pending' | 'ready' | 'failed';
  originalFilename: string;
  contentType: string;
  sizeBytes: number | null;
  originalSizeBytes: number | null;
  thumbKey: string | null;
  needsServerTranscode: boolean;
}

export interface GateFailure {
  lineItemId?: string;
  message: string;
}

export interface ReportDetail {
  report: {
    id: string;
    type: 'monthly' | 'travel';
    status: ReportStatus;
    title: string;
    periodMonth: string | null;
    tripId: string | null;
    submittedTotalAmount: string | null;
    submittedAt: string | null;
    decidedAt: string | null;
    decisionNote: string | null;
    paymentReference: string | null;
    userId: string;
  };
  owner: { fullName: string; email: string } | null;
  trip: {
    id: string; name: string; destination: string; purpose: string | null;
    startDate: string; endDate: string; perDiemRateId: string | null;
  } | null;
  lineItems: LineItem[];
  receipts: Receipt[];
  total: string;
  submitBlockers: GateFailure[];
  canEdit: boolean;
}
